import { useEffect, useState } from 'react';
import { api, ErreurApi } from '../api.js';
import { tokens } from '../tokens.js';
import {
  Badge, BoiteErreur, labelChamp, inputBase,
  boutonPrincipal, boutonSecondaire, formaterDateRelative, formaterDateLongue,
} from '../helpers.jsx';
import {
  Plus, RefreshCw, Trash2, Edit, AlertTriangle, CheckCircle,
  Settings, Activity, Zap, Eye, EyeOff,
} from 'lucide-react';

const NIVEAUX = [
  { val: 'error',   label: 'Error et + (recommandé)' },
  { val: 'warning', label: 'Warning et +' },
  { val: 'info',    label: 'Info et + (verbose)' },
  { val: 'fatal',   label: 'Uniquement fatal' },
];

export function AdminSentry() {
  const [config, setConfig]     = useState(null);
  const [projets, setProjets]   = useState([]);
  const [services, setServices] = useState([]);
  const [chargement, setCh]     = useState(true);
  const [erreur, setErreur]     = useState('');
  // Edit config
  const [tokenInput, setTokenInput] = useState('');
  const [tokenChange, setTokenChange] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  // Test
  const [testRetour, setTestR]  = useState(null);
  const [testEnCours, setTestEC] = useState(false);
  // Ajouter projet
  const [ajouterOuvert, setAjouter] = useState(false);
  // Sync
  const [syncEnCours, setSyncEC] = useState(null);
  const [syncRetour, setSyncR]   = useState(null);

  function recharger() {
    setCh(true);
    Promise.all([
      api.get('/admin/sentry/config').then(d => {
        setConfig(d.config);
        setUrlInput(d.config?.url_base || 'https://sentry.io');
      }),
      api.get('/admin/sentry/projets').then(d => setProjets(d.projets || [])),
      api.get('/admin/services').then(d => setServices(applatir(d.services || []))).catch(() => {}),
    ])
      .catch(e => setErreur(e.message))
      .finally(() => setCh(false));
  }
  useEffect(recharger, []);

  async function sauverConfig() {
    setErreur('');
    const corps = { url_base: urlInput };
    if (tokenChange) {
      corps.token_api = tokenInput.trim() === '' ? '__effacer__' : tokenInput.trim();
    }
    try {
      const rep = await api.put('/admin/sentry/config', corps);
      setConfig(rep.config);
      setTokenInput(''); setTokenChange(false);
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Erreur');
    }
  }

  async function tester() {
    setTestEC(true); setTestR(null);
    try {
      const rep = await api.post('/admin/sentry/test', {});
      setTestR(rep);
    } catch (e) {
      setTestR({ ok: false, erreur: e instanceof ErreurApi ? e.message : 'Erreur' });
    } finally { setTestEC(false); }
  }

  async function syncProjet(id, libelle) {
    setSyncEC(id); setSyncR(null);
    try {
      const rep = await api.post(`/admin/sentry/projets/${id}/sync`, {});
      setSyncR({ id, libelle, ...rep });
      // Recharge liste pour avoir les nouvelles dates
      const d = await api.get('/admin/sentry/projets');
      setProjets(d.projets || []);
    } catch (e) {
      setSyncR({ id, libelle, erreurs: [e.message] });
    } finally { setSyncEC(null); }
  }

  async function basculer(p) {
    try {
      await api.put(`/admin/sentry/projets/${p.id}`, { actif: !p.actif });
      recharger();
    } catch (e) { setErreur(e instanceof ErreurApi ? e.message : 'Erreur'); }
  }

  async function supprimer(p) {
    if (!confirm(`Supprimer la configuration Sentry "${p.libelle}" ? Les tickets déjà créés restent.`)) return;
    try {
      await api.delete(`/admin/sentry/projets/${p.id}`);
      recharger();
    } catch (e) { setErreur(e instanceof ErreurApi ? e.message : 'Erreur'); }
  }

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: tokens.ink }}>
          Intégration Sentry
        </h2>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: tokens.inkSoft }}>
          Les issues Sentry sont rapatriées comme tickets. Les doublons sont fusionnés
          (compteur d'occurrences). Pas de mail envoyé sur les tickets Sentry — Sentry alerte déjà.
        </p>
      </div>

      {erreur && <BoiteErreur message={erreur} />}
      {chargement && <div style={etat}>Chargement…</div>}

      {!chargement && (
        <>
          {/* ===== Config globale ===== */}
          <div style={cadre}>
            <h3 style={titreSection}>
              <Settings size={14} /> Configuration globale
            </h3>

            <div style={{ marginBottom: 14 }}>
              <label style={labelChamp}>URL Sentry</label>
              <input
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                style={inputBase}
                placeholder="https://sentry.io"
              />
              <div style={hint}>Sentry SaaS = https://sentry.io. Pour du self-hosted, mets ton URL.</div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={labelChamp}>
                Token API
                {config?.token_configure && (
                  <Badge bg={tokens.successBg} fg={tokens.success} taille="petit"> ✓ configuré</Badge>
                )}
              </label>
              {!tokenChange ? (
                <button
                  onClick={() => setTokenChange(true)}
                  style={{ ...boutonSecondaire, fontSize: 12 }}
                >
                  {config?.token_configure ? 'Modifier le token' : 'Saisir un token'}
                </button>
              ) : (
                <div>
                  <input
                    type="password"
                    value={tokenInput}
                    onChange={(e) => setTokenInput(e.target.value)}
                    style={inputBase}
                    placeholder="sntrys_..."
                    autoFocus
                  />
                  <div style={{ display:'flex', gap: 6, marginTop: 6 }}>
                    <button onClick={() => { setTokenChange(false); setTokenInput(''); }} style={{ ...boutonSecondaire, fontSize: 12 }}>
                      Annuler
                    </button>
                    {config?.token_configure && (
                      <button onClick={() => { setTokenInput('__effacer__'); }} style={{ ...boutonSecondaire, fontSize: 12, color: tokens.danger }}>
                        Effacer le token actuel
                      </button>
                    )}
                  </div>
                </div>
              )}
              <div style={hint}>
                À générer depuis Sentry &gt; User Settings &gt; Auth Tokens.<br/>
                Scopes requis : <code>org:read</code>, <code>project:read</code>, <code>event:read</code>.
              </div>
            </div>

            <div style={{ display:'flex', gap: 8, alignItems:'center' }}>
              <button onClick={sauverConfig} style={boutonPrincipal}>Enregistrer</button>
              <button onClick={tester} disabled={testEnCours || !config?.token_configure} style={boutonSecondaire}>
                <Zap size={13} /> {testEnCours ? 'Test…' : 'Tester le token'}
              </button>
            </div>

            {testRetour && (
              <div style={{
                marginTop: 12, padding: 12, borderRadius: 8,
                background: testRetour.ok ? tokens.successBg : tokens.dangerBg,
                color: testRetour.ok ? tokens.success : tokens.danger,
                fontSize: 13,
              }}>
                {testRetour.ok ? (
                  <div>
                    <strong>✓ Token valide.</strong> Organisations accessibles :
                    <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
                      {testRetour.organisations.map(o => (
                        <li key={o.slug}><code>{o.slug}</code> — {o.name}</li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div><strong>✗ Échec :</strong> {testRetour.erreur}</div>
                )}
              </div>
            )}
          </div>

          {/* ===== Projets ===== */}
          <div style={{ marginTop: 22 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: tokens.inkSoft, textTransform:'uppercase', letterSpacing: 0.5 }}>
                Projets Sentry mappés ({projets.length})
              </h3>
              <button onClick={() => setAjouter(true)} style={boutonPrincipal} disabled={!config?.token_configure}>
                <Plus size={14} /> Ajouter un projet
              </button>
            </div>

            {projets.length === 0 ? (
              <div style={etat}>
                <Activity size={28} color={tokens.inkMuted} />
                <div style={{ marginTop: 8 }}>Aucun projet Sentry configuré.</div>
                {!config?.token_configure && (
                  <div style={{ marginTop: 4, fontSize: 12, color: tokens.warning }}>
                    Configure d'abord le token API ci-dessus.
                  </div>
                )}
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap: 10 }}>
                {projets.map(p => (
                  <CarteProjet
                    key={p.id} projet={p}
                    syncEnCours={syncEnCours === p.id}
                    syncRetour={syncRetour && syncRetour.id === p.id ? syncRetour : null}
                    onSync={() => syncProjet(p.id, p.libelle)}
                    onBasculer={() => basculer(p)}
                    onSupprimer={() => supprimer(p)}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {ajouterOuvert && (
        <ModaleAjouter
          services={services}
          onAnnuler={() => setAjouter(false)}
          onCree={() => { setAjouter(false); recharger(); }}
        />
      )}
    </div>
  );
}

function CarteProjet({ projet, syncEnCours, syncRetour, onSync, onBasculer, onSupprimer }) {
  const cheminService = projet.parent_service_nom
    ? `${projet.parent_service_nom} / ${projet.service_nom}`
    : projet.service_nom;

  return (
    <div style={{
      background: tokens.surface,
      border: `1px solid ${tokens.border}`,
      borderRadius: 10,
      padding: 14,
      opacity: projet.actif ? 1 : 0.6,
    }}>
      <div style={{ display:'flex', alignItems:'center', gap: 10, marginBottom: 8 }}>
        <Activity size={16} color={projet.actif ? tokens.accent : tokens.inkMuted} />
        <strong style={{ fontSize: 14, color: tokens.ink }}>{projet.libelle}</strong>
        {!projet.actif && <Badge bg={tokens.dangerBg} fg={tokens.danger} taille="petit">Inactif</Badge>}
        <span style={{ fontSize: 11, color: tokens.inkMuted, fontFamily:'monospace' }}>
          {projet.organisation_slug}/{projet.projet_slug}
        </span>
        <div style={{ marginLeft:'auto', display:'flex', gap: 6 }}>
          <button onClick={onSync} disabled={syncEnCours} style={{ ...boutonPrincipal, padding: '5px 10px', fontSize: 12 }}>
            <RefreshCw size={12} className={syncEnCours ? 'spin' : ''} /> {syncEnCours ? 'Sync…' : 'Sync now'}
          </button>
          <button onClick={onBasculer} style={{ ...boutonSecondaire, padding: '5px 10px', fontSize: 12 }}>
            {projet.actif ? 'Désactiver' : 'Activer'}
          </button>
          <button onClick={onSupprimer} style={{ ...boutonSecondaire, padding: '5px 8px', fontSize: 12, color: tokens.danger }}>
            <Trash2 size={12} />
          </button>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, fontSize: 12, color: tokens.inkSoft }}>
        <div>
          <div style={metaLabel}>Service de destination</div>
          <div>{cheminService}</div>
        </div>
        <div>
          <div style={metaLabel}>Niveau min</div>
          <div>{projet.niveau_min}</div>
        </div>
        <div>
          <div style={metaLabel}>Tickets liés</div>
          <div>{projet.nb_tickets_lies}</div>
        </div>
        <div>
          <div style={metaLabel}>Dernière sync</div>
          <div>
            {projet.derniere_sync ? (
              <>
                {formaterDateRelative(projet.derniere_sync)}
                {' '}
                {projet.derniere_sync_ok
                  ? <CheckCircle size={11} color={tokens.success} style={{ verticalAlign:'middle' }} />
                  : <AlertTriangle size={11} color={tokens.danger} style={{ verticalAlign:'middle' }} />}
              </>
            ) : (
              <span style={{ color: tokens.inkMuted }}>Jamais</span>
            )}
          </div>
        </div>
      </div>

      {projet.derniere_sync_erreur && (
        <div style={{
          marginTop: 10, padding: '8px 10px',
          background: tokens.dangerBg, color: tokens.danger,
          fontSize: 12, borderRadius: 6,
        }}>
          <strong>Erreur dernière sync :</strong> {projet.derniere_sync_erreur}
        </div>
      )}

      {syncRetour && (
        <div style={{
          marginTop: 10, padding: '8px 10px',
          background: (syncRetour.erreurs?.length ?? 0) === 0 ? tokens.successBg : tokens.warningBg,
          color: (syncRetour.erreurs?.length ?? 0) === 0 ? tokens.success : tokens.warning,
          fontSize: 12, borderRadius: 6,
        }}>
          ✓ {syncRetour.crees || 0} créé(s) · {syncRetour.mis_a_jour || 0} mis à jour
          {syncRetour.erreurs?.length > 0 && (
            <div style={{ marginTop: 4 }}>
              {syncRetour.erreurs.length} erreur(s) : {syncRetour.erreurs.slice(0, 2).join(' / ')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ModaleAjouter({ services, onAnnuler, onCree }) {
  const [lib, setLib] = useState('');
  const [org, setOrg] = useState('');
  const [proj, setProj] = useState('');
  const [svc, setSvc] = useState('');
  const [niv, setNiv] = useState('error');
  const [envoi, setEnvoi] = useState(false);
  const [err, setErr] = useState('');

  async function valider() {
    if (!lib || !org || !proj || !svc) { setErr('Tous les champs sont requis'); return; }
    setEnvoi(true); setErr('');
    try {
      await api.post('/admin/sentry/projets', {
        libelle: lib.trim(),
        organisation_slug: org.trim(),
        projet_slug: proj.trim(),
        service_id: Number(svc),
        niveau_min: niv,
      });
      onCree();
    } catch (e) {
      setErr(e instanceof ErreurApi ? e.message : 'Erreur');
    } finally { setEnvoi(false); }
  }

  return (
    <div style={{
      position:'fixed', inset: 0, zIndex: 50, background: 'rgba(0,0,0,0.5)',
      display:'flex', alignItems:'center', justifyContent:'center', padding: 20,
    }}>
      <div style={{
        background: tokens.surface, borderRadius: 12, maxWidth: 480, width:'100%',
        padding: 24, boxShadow: '0 10px 40px rgba(0,0,0,0.20)',
      }}>
        <h3 style={{ margin: '0 0 14px', fontSize: 16, fontWeight: 600 }}>Ajouter un projet Sentry</h3>

        <label style={labelChamp}>Libellé interne</label>
        <input value={lib} onChange={(e) => setLib(e.target.value)} placeholder="Ex : AMC Paie production" style={inputBase} autoFocus />

        <label style={{ ...labelChamp, marginTop: 12 }}>Slug organisation Sentry</label>
        <input value={org} onChange={(e) => setOrg(e.target.value)} placeholder="Ex : am-consultants" style={{ ...inputBase, fontFamily:'monospace', fontSize: 13 }} />

        <label style={{ ...labelChamp, marginTop: 12 }}>Slug projet Sentry</label>
        <input value={proj} onChange={(e) => setProj(e.target.value)} placeholder="Ex : amc-paie" style={{ ...inputBase, fontFamily:'monospace', fontSize: 13 }} />
        <div style={hint}>
          Visible dans l'URL Sentry : <code>sentry.io/organizations/{'{org}'}/projects/{'{projet}'}/</code>
        </div>

        <label style={{ ...labelChamp, marginTop: 12 }}>Service de destination</label>
        <select value={svc} onChange={(e) => setSvc(e.target.value)} style={inputBase}>
          <option value="">— Choisis un service —</option>
          {services.map(s => <option key={s.id} value={s.id}>{s.chemin}</option>)}
        </select>
        <div style={hint}>Les tickets créés depuis ce projet Sentry seront rattachés à ce service.</div>

        <label style={{ ...labelChamp, marginTop: 12 }}>Niveau minimum à importer</label>
        <select value={niv} onChange={(e) => setNiv(e.target.value)} style={inputBase}>
          {NIVEAUX.map(n => <option key={n.val} value={n.val}>{n.label}</option>)}
        </select>

        {err && <BoiteErreur message={err} />}

        <div style={{ display:'flex', gap: 8, justifyContent:'flex-end', marginTop: 16 }}>
          <button onClick={onAnnuler} style={boutonSecondaire}>Annuler</button>
          <button onClick={valider} disabled={envoi} style={boutonPrincipal}>
            {envoi ? 'Création…' : 'Créer'}
          </button>
        </div>
      </div>
    </div>
  );
}

function applatir(arbre) {
  const out = [];
  for (const s of arbre) {
    out.push({ id: s.id, chemin: s.nom });
    if (s.sous_services) {
      for (const ss of s.sous_services) {
        out.push({ id: ss.id, chemin: `${s.nom} / ${ss.nom}` });
      }
    }
  }
  return out;
}

const cadre = {
  background: tokens.surface,
  border: `1px solid ${tokens.border}`,
  borderRadius: 10,
  padding: 18,
};
const titreSection = {
  margin: '0 0 14px', fontSize: 13, fontWeight: 600, color: tokens.inkSoft,
  textTransform:'uppercase', letterSpacing: 0.5,
  display:'flex', alignItems:'center', gap: 6,
};
const hint = {
  marginTop: 4, fontSize: 11, color: tokens.inkMuted, lineHeight: 1.4,
};
const metaLabel = {
  fontSize: 10, fontWeight: 600, color: tokens.inkMuted,
  textTransform:'uppercase', letterSpacing: 0.4, marginBottom: 2,
};
const etat = {
  background: tokens.surface,
  border: `1px solid ${tokens.border}`,
  borderRadius: 10,
  padding: 32,
  textAlign:'center',
  color: tokens.inkSoft,
  fontSize: 14,
  display:'flex', flexDirection:'column', alignItems:'center', gap: 6,
};
